import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { TBoxBodyPublic } from '@lbs/shared-rp-types'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { green, red, yellow } from './Notice'

type TColor = 'yellow' | 'green' | 'red'

// Animations for bet status indicators
const appearAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  70% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

const highlightAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 196, 69, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(255, 196, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 196, 69, 0); }
`

const disappearAnimation = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.5);
  }
`

// Container for all bet status indicators
const BetStatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  margin-top: 15px;
  width: 100%;
`

// Row of bet status indicators
const BetStatusRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  //height: 45px;
`

// Individual bet status indicator
interface BetStatusIndicatorProps {
  color: TColor
  animationStatus?: 'added' | 'updated' | 'removed' | null
}

const BetStatusIndicator = styled.div<BetStatusIndicatorProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: bold;
  gap: 8px;
  background-color: ${({ color }) => {
    switch (color) {
      case 'green':
        return 'rgba(139, 229, 137, 0.15)'
      case 'red':
        return 'rgba(237, 28, 36, 0.15)'
      default:
        return 'rgba(255, 196, 69, 0.15)'
    }
  }};
  border: 2px solid
    ${({ color }) => {
      switch (color) {
        case 'green':
          return 'rgba(139, 229, 137, 0.5)'
        case 'red':
          return 'rgba(237, 28, 36, 0.5)'
        default:
          return 'rgba(255, 196, 69, 0.5)'
      }
    }};
  color: ${({ color }) => {
    switch (color) {
      case 'green':
        return green
      case 'red':
        return red
      default:
        return yellow
    }
  }};
  animation: ${({ animationStatus }) => {
    switch (animationStatus) {
      case 'added':
        return `${appearAnimation} 0.5s ease-out forwards`
      case 'updated':
        return `${highlightAnimation} 1.5s infinite`
      case 'removed':
        return `${disappearAnimation} 0.5s ease-in forwards`
      default:
        return 'none'
    }
  }};
`

// Status icon
const StatusIcon = styled.div<BetStatusIndicatorProps>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ color }) => {
    switch (color) {
      case 'green':
        return green
      case 'red':
        return red
      default:
        return yellow
    }
  }};
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
  font-size: 12px;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.7);
  animation: ${({ animationStatus }) => {
    switch (animationStatus) {
      case 'added':
        return `${appearAnimation} 0.5s ease-out forwards`
      case 'updated':
        return `${highlightAnimation} 1.5s infinite`
      case 'removed':
        return `${disappearAnimation} 0.5s ease-in forwards`
      default:
        return 'none'
    }
  }};
`

type TStake = {
  name: string
  iconName: string
  value: number
  color: () => TColor
}

const BetStatusIndicators: FC<{
  box: TBoxBodyPublic
}> = observer(({ box }) => {
  const { ante, bonus, insurance, bet } = box

  const anteData: TStake = {
    name: 'Ante',
    iconName: 'A',
    value: ante,
    color: () => 'yellow',
  }

  const bonusData: TStake = {
    name: 'Bonus',
    iconName: 'B',
    value: bonus.amount,
    color: () => {
      if (bonus.result === 'win') return 'green'
      if (bonus.result === 'lose') return 'red'
      return 'yellow'
    },
  }

  const insuranceData: TStake = {
    name: 'Insurance',
    iconName: 'I',
    value: insurance.cost,
    color: () => {
      if (bonus.result === 'win') return 'green'
      if (bonus.result === 'lose') return 'red'

      return 'yellow'
    },
  }

  const betData: TStake = {
    name: 'Bet',
    iconName: 'B',
    value: bet,
    color: () => 'yellow',
  }

  const stakes: Array<TStake> = [anteData, bonusData, insuranceData, betData]

  return (
    <BetStatusContainer>
      <BetStatusRow>
        {stakes
          .filter((stake) => stake.value > 0)
          .map(({ name, iconName, color }) => {
            return (
              <BetStatusIndicator color={color()} key={name} title={name}>
                <StatusIcon color={color()}>{iconName}</StatusIcon>
                {name}
              </BetStatusIndicator>
            )
          })}
      </BetStatusRow>
    </BetStatusContainer>
  )
})

export default BetStatusIndicators
